import { urlEndPoint } from '../Config';
import {
  callApi,
  callApiInit,
  callApiLogin,
  getTokenView,
  descryptedaes256cbc,
  callApiRegister,
  callSendFornData,
} from './utils';

/**
 * The function `getToken` retrieves an access token from local storage, decrypts it if a certain
 * condition is met, and returns the access token.
 * @returns the `access_token` property of the `token` object.
 */
function getToken() {
  let token = '';
  try {
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return token;
    }

    // Primero intentar desencriptar
    const localToken = getTokenView(localStorage.getItem('session'));
    if (localToken) {
      try {
        const decrypted = descryptedaes256cbc(ceaData, localToken);
        if (decrypted && decrypted.access_token) {
          return decrypted.access_token;
        }
      } catch (decryptError) {
        // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
        try {
          const tokenData = JSON.parse(ceaData);
          if (tokenData && tokenData.access_token) {
            return tokenData.access_token;
          }
        } catch (jsonError) {
          console.error("Error obteniendo token:", jsonError);
        }
      }
    } else {
      // Si no hay localToken, intentar JSON directamente
      try {
        const tokenData = JSON.parse(ceaData);
        if (tokenData && tokenData.access_token) {
          return tokenData.access_token;
        }
      } catch (jsonError) {
        console.error("Error obteniendo token:", jsonError);
      }
    }
  } catch (error) {
    console.error("Error obteniendo token:", error);
  }
  return token;
}

//VERIFY TOKEN
export const apiGetVerifyToken = async () => {
  const url = `${urlEndPoint}/api/v1/users/verify`;
  const json = await callApi(url, 'GET', getToken(), null);
  // console.log('json', json);
  return json;
};

//SESSION TOKEN
export const apiGetSession = async () => {
  const url = `${urlEndPoint}/api/v3/auth/init`;
  const json = await callApiInit(url, 'GET', null, null);
  return json;
};
/**
 * The function `apiVerifyTokenRecovery` sends a POST request to the specified URL with the provided
 * body and returns the response as JSON.
 * @param body - The `body` parameter is an object that contains the data to be sent in the request
 * body. It typically includes information required for token recovery, such as the user's email or
 * username.
 * @returns the JSON response from the API call.
 */
export const apiVerifyTokenRecovery = async (body) => {
  const url = `${urlEndPoint}/api/v3/auth/token/recovery`;
  const json = await callApi(url, 'POST', null, body);
  return json;
};

/**
 * The function `apiGetMe` makes a GET request to the `/api/v3/users/get/me` endpoint and returns the
 * response as JSON.
 * @returns The function `apiGetMe` is returning a JSON object.
 */

export const apiListUsers = async () => {
  const url = `${urlEndPoint}/api/v1/users/list`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};
export const apiCreateUser = async (body) => {
  const url = `${urlEndPoint}/api/v1/users/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdateUserData = async (id, userData) => {
  if (!id) throw new Error('El ID del usuario es requerido para la actualización.');
  const url = `${urlEndPoint}/api/v1/users/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), userData); // Se pasan los datos como userData
  return json;
};

export const apiDeleteUser = async (body) => {
  const url = `${urlEndPoint}/api/v1/users/delete`;
  const json = await callApi(url, 'DELETE', getToken(), body);

  return json;
};

// Companies API
export const apiListCompanies = async () => {
  const url = `${urlEndPoint}/api/v1/companies`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetCompany = async (id) => {
  const url = `${urlEndPoint}/api/v1/companies/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateCompany = async (body) => {
  const url = `${urlEndPoint}/api/v1/companies`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiCreateCompanyWithAdmin = async (body) => {
  const url = `${urlEndPoint}/api/v1/companies/with-admin`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiCheckSlugAvailable = async (slug) => {
  const url = `${urlEndPoint}/api/v1/onboarding/slug-available/${encodeURIComponent(slug)}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiUpdateCompany = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/companies/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiDeleteCompany = async (id) => {
  const url = `${urlEndPoint}/api/v1/companies/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiToggleCompanyActive = async (id) => {
  const url = `${urlEndPoint}/api/v1/companies/${id}/toggle-active`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

export const apiResendWelcomeEmail = async (id) => {
  const url = `${urlEndPoint}/api/v1/companies/${id}/resend-welcome-email`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiGetGlobalStatistics = async () => {
  const url = `${urlEndPoint}/api/v1/companies/statistics/global`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

// Plans API
export const apiListPlans = async () => {
  const url = `${urlEndPoint}/api/v1/plans`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreatePlan = async (body) => {
  const url = `${urlEndPoint}/api/v1/plans`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdatePlan = async (planId, body) => {
  const url = `${urlEndPoint}/api/v1/plans/${planId}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiDeletePlan = async (planId) => {
  const url = `${urlEndPoint}/api/v1/plans/${planId}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiPreviewPlanMigration = async (planCode) => {
  const url = `${urlEndPoint}/api/v1/plans/${planCode}/migration-preview`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiSchedulePlanMigration = async (planCode, body) => {
  const url = `${urlEndPoint}/api/v1/plans/${planCode}/migrations`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiListPlanMigrations = async () => {
  const url = `${urlEndPoint}/api/v1/plans/migrations`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCancelPlanMigration = async (migrationId) => {
  const url = `${urlEndPoint}/api/v1/plans/migrations/${migrationId}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiApplyPlanMigration = async (migrationId) => {
  const url = `${urlEndPoint}/api/v1/plans/migrations/${migrationId}/apply`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

// Subscriptions API
export const apiListSubscriptions = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiSendDeletionWarning = async (subscriptionId) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/${subscriptionId}/send-deletion-warning`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiSendExpiringSoon = async (subscriptionId) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/${subscriptionId}/send-expiring-soon`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiSendExpiringWarning = async (subscriptionId) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/${subscriptionId}/send-expiring-warning`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiGetActiveSubscriptions = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/active`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetSubscriptionStatistics = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/statistics`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateSubscription = async (body) => {
  const url = `${urlEndPoint}/api/v1/subscriptions`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdateSubscription = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiDeleteSubscription = async (id) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiGetSubscriptionByCompany = async (companyId) => {
  const url = `${urlEndPoint}/api/v1/subscriptions/company/${companyId}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetSubscriptionAlerts = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/alerts`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetBusinessMetrics = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/business-metrics`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

// Payments API
export const apiListPayments = async (subscriptionId = null) => {
  const url = subscriptionId 
    ? `${urlEndPoint}/api/v1/payments?subscriptionId=${subscriptionId}`
    : `${urlEndPoint}/api/v1/payments`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetPayment = async (id) => {
  const url = `${urlEndPoint}/api/v1/payments/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetPaymentsBySubscription = async (subscriptionId) => {
  const url = `${urlEndPoint}/api/v1/payments/subscription/${subscriptionId}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreatePayment = async (body) => {
  const url = `${urlEndPoint}/api/v1/payments`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdatePayment = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/payments/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiDeletePayment = async (id) => {
  const url = `${urlEndPoint}/api/v1/payments/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiGetPaymentStatistics = async () => {
  const url = `${urlEndPoint}/api/v1/payments/statistics`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

// Notifications API
export const apiListNotifications = async (companyId = null, unreadOnly = false) => {
  let url = `${urlEndPoint}/api/v1/notifications`;
  const params = [];
  if (companyId) params.push(`companyId=${companyId}`);
  if (unreadOnly) params.push('unreadOnly=true');
  if (params.length > 0) url += `?${params.join('&')}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetNotification = async (id) => {
  const url = `${urlEndPoint}/api/v1/notifications/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetUnreadNotificationCount = async (companyId = null) => {
  let url = `${urlEndPoint}/api/v1/notifications/unread/count`;
  // Solo agregar companyId si es un número válido
  if (companyId !== null && companyId !== undefined && companyId !== '' && !isNaN(Number(companyId))) {
    url += `?companyId=${companyId}`;
  }
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateNotification = async (body) => {
  const url = `${urlEndPoint}/api/v1/notifications`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdateNotification = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/notifications/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiMarkNotificationAsRead = async (id) => {
  const url = `${urlEndPoint}/api/v1/notifications/${id}/read`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

export const apiMarkAllNotificationsAsRead = async (companyId = null) => {
  let url = `${urlEndPoint}/api/v1/notifications/mark-all-read`;
  if (companyId) url += `?companyId=${companyId}`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

export const apiArchiveNotification = async (id) => {
  const url = `${urlEndPoint}/api/v1/notifications/${id}/archive`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

export const apiDeleteNotification = async (id) => {
  const url = `${urlEndPoint}/api/v1/notifications/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

// Reports API
export const apiGetFinancialReport = async (startDate = null, endDate = null) => {
  let url = `${urlEndPoint}/api/v1/reports/financial`;
  const params = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetUsageReport = async (companyId = null) => {
  let url = `${urlEndPoint}/api/v1/reports/usage`;
  if (companyId) url += `?companyId=${companyId}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetGrowthReport = async (months = 12) => {
  const url = `${urlEndPoint}/api/v1/reports/growth?months=${months}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiGetConsolidatedReport = async (startDate = null, endDate = null) => {
  let url = `${urlEndPoint}/api/v1/reports/consolidated`;
  const params = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

const buildNpsQuery = (filters = {}) => {
  const params = [];
  if (filters.startDate) params.push(`startDate=${filters.startDate}`);
  if (filters.endDate) params.push(`endDate=${filters.endDate}`);
  if (filters.companyId) params.push(`companyId=${filters.companyId}`);
  if (filters.plan) params.push(`plan=${encodeURIComponent(filters.plan)}`);
  if (filters.mode) params.push(`mode=${filters.mode}`);
  if (filters.category) params.push(`category=${filters.category}`);
  if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
  if (filters.months) params.push(`months=${filters.months}`);
  return params.length > 0 ? `?${params.join('&')}` : '';
};

export const apiGetNpsSummary = async (filters = {}) => {
  const url = `${urlEndPoint}/api/v1/nps/summary${buildNpsQuery(filters)}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiGetNpsTrend = async (filters = {}) => {
  const url = `${urlEndPoint}/api/v1/nps/trend${buildNpsQuery({
    months: filters.months || 12,
    mode: filters.mode,
  })}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiGetNpsResponses = async (filters = {}) => {
  const url = `${urlEndPoint}/api/v1/nps/responses${buildNpsQuery(filters)}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiGetCompanyNps = async (companyId) => {
  const url = `${urlEndPoint}/api/v1/nps/company/${companyId}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiSendNpsEmail = async (companyId) => {
  const url = `${urlEndPoint}/api/v1/nps/company/${companyId}/send-email`;
  return callApi(url, 'POST', getToken(), null);
};

export const apiGetCompanyStatistics = async (companyId) => {
  const url = `${urlEndPoint}/api/v1/companies/${companyId}/statistics`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateRoom = async (body) => {
  const url = `${urlEndPoint}/api/v1/rooms/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiListRooms = async () => {
  const url = `${urlEndPoint}/api/v1/rooms/list`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};
export const apiUpdateRoom = async (id, roomData) => {
  //if (!id) throw new Error('El ID del usuario es requerido para la actualización.');
  const url = `${urlEndPoint}/api/v1/rooms/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), roomData); // Se pasan los datos como userData
  return json;
};

export const apiSetRoomActive = async (id, isActive) => {
  const url = `${urlEndPoint}/api/v1/rooms/active/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), { isActive });

  return json;
};

export const apiGuestFindRut = async (body) => {
  const url = `${urlEndPoint}/api/v1/guest/find-by-rut`;
  const json = await callApi(url, 'POST', getToken(), body);

  return json;
};

export const apiGuestCreate = async (body) => {
  const url = `${urlEndPoint}/api/v1/guest/create`;
  const json = await callApi(url, 'POST', getToken(), body);

  return json;
};

export const apiBookingCreate = async (body) => {
  const url = `${urlEndPoint}/api/v1/booking/create`;
  const json = await callApi(url, 'POST', getToken(), body);

  return json;
};

export const apiListBookingConfirmed = async () => {
  const url = `${urlEndPoint}/api/v1/booking/confirmed`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiPerformCheckout = async (bookingId, notes) => {
  const url = `${urlEndPoint}/api/v1/booking/checkout/${bookingId}`;
  const payload = { notes }; // Solo las notas van en el cuerpo
  const json = await callApi(url, 'PATCH', getToken(), payload); // Envía las notas como cuerpo
  return json;
};
export const apiListGuest = async () => {
  const url = `${urlEndPoint}/api/v1/guest/list`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiUpdateGuestData = async (id, guestData) => {
  if (!id) throw new Error('El ID del usuario es requerido para la actualización.');
  const url = `${urlEndPoint}/api/v1/guest/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), guestData); // Se pasan los datos como userData
  return json;
};


export const apiListGuestID = async (id) => {
  const url = `${urlEndPoint}/api/v1/guest/list/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiListGuestHistories = async (id) => {
  const url = `${urlEndPoint}/api/v1/booking/guest-history/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiUpdateBooking = async (id, BData) => {
  const url = `${urlEndPoint}/api/v1/booking/update/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), BData);
  return json;
};

export const apiListBokingID = async (id) => {
  const url = `${urlEndPoint}/api/v1/booking/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

// Cron Configuration API
export const apiGetCronConfig = async () => {
  const url = `${urlEndPoint}/api/v1/booking/cron/config`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiUpdateCronConfig = async (configData) => {
  const url = `${urlEndPoint}/api/v1/booking/cron/config`;
  const json = await callApi(url, 'PATCH', getToken(), configData);
  return json;
};

// Branding API (calendario tipo Doodle del login de clients)
export const apiGetBranding = async () => {
  const url = `${urlEndPoint}/api/v1/branding`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiUpdateBranding = async (body) => {
  const url = `${urlEndPoint}/api/v1/branding`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiUploadLoginBackground = async (file) => {
  const url = `${urlEndPoint}/api/v1/branding/login-background`;
  const formData = new FormData();
  formData.append('file', file);
  return callSendFornData(url, getToken(), formData);
};

export const apiResetLoginBackground = async () => {
  const url = `${urlEndPoint}/api/v1/branding/login-background`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiListLoginDoodles = async () => {
  const url = `${urlEndPoint}/api/v1/branding/doodles`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateLoginDoodle = async (body) => {
  const url = `${urlEndPoint}/api/v1/branding/doodles`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

export const apiUpdateLoginDoodle = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/branding/doodles/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

export const apiDeleteLoginDoodle = async (id) => {
  const url = `${urlEndPoint}/api/v1/branding/doodles/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

export const apiUploadBrandingImage = async (file) => {
  const url = `${urlEndPoint}/api/v1/branding/upload`;
  const formData = new FormData();
  formData.append('file', file);
  return callSendFornData(url, getToken(), formData);
};

export const apiUploadDoodleImage = async (id, file) => {
  const url = `${urlEndPoint}/api/v1/branding/doodles/${id}/image`;
  const formData = new FormData();
  formData.append('file', file);
  return callSendFornData(url, getToken(), formData);
};

// Ejecutar crones manualmente - Bookings
export const apiExecuteActivateBookings = async () => {
  const url = `${urlEndPoint}/api/v1/booking/cron/execute/activate-bookings`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteCheckUpcomingBookings = async () => {
  const url = `${urlEndPoint}/api/v1/booking/cron/execute/check-upcoming-bookings`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteCheckCheckouts = async () => {
  const url = `${urlEndPoint}/api/v1/booking/cron/execute/check-checkouts`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteUpdateRoomStates = async () => {
  const url = `${urlEndPoint}/api/v1/booking/cron/execute/update-room-states`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

// Ejecutar crones manualmente - Subscriptions
export const apiExecuteCheckExpiringSubscriptions = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/cron/execute/check-expiring-subscriptions`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteDeactivateExpiredSubscriptions = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/cron/execute/deactivate-expired-subscriptions`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteCheckSubscriptionsHourly = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/cron/execute/check-subscriptions-hourly`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

export const apiExecuteSendDataDeletionWarnings = async () => {
  const url = `${urlEndPoint}/api/v1/subscriptions/cron/execute/send-data-deletion-warnings`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

// Ejecutar crones manualmente - Plans
export const apiExecuteApplyDuePlanMigrations = async () => {
  const url = `${urlEndPoint}/api/v1/plans/cron/execute/apply-due-plan-migrations`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};

// Ejecutar crones manualmente - Notifications
export const apiExecuteDeleteOldNotifications = async () => {
  const url = `${urlEndPoint}/api/v1/notifications/cron/execute/delete-old-notifications`;
  const json = await callApi(url, 'POST', getToken(), null);
  return json;
};



//LOGIN
//ver esto despues 
export const apiGetLogin = async (body) => {
  const url = `${urlEndPoint}/api/v3/auth/signin`; 
  const json = await callApiLogin(url, 'POST', null, body);
  return json;
};


export const apiRegisterLogin = async (body) => {
  const url = `${urlEndPoint}/api/v3/auth/business/signup`;
  const json = await callApiRegister(url, 'POST', null, body);

  return json;
};


export const apiLogin = async (requestData) => {
  const url = `${urlEndPoint}/api/v3/auth/signin`;
  const json = await callApiLogin(url, 'POST', null, requestData);
  return json;
};

export const getRecoveryPass = async (requestData) => {
  const url = `${urlEndPoint}/api/v3/auth/recovery`;
  const json = await callApi(url, 'POST', null, requestData);
  return json;
};

export const apiUpdatePass = async (body) => {
  const url = `${urlEndPoint}/api/v3/auth/token/pass`;
  const json = await callApi(url, 'PUT', getToken(), body);
  return json;
};

/**
 * Refresca el access token usando el refresh token
 */
export const apiRefreshToken = async (refreshToken) => {
  const url = `${urlEndPoint}/api/v3/auth/refresh`;
  const json = await callApi(url, 'POST', null, { refresh_token: refreshToken });
  return json;
};

/**
 * Cierra sesión revocando el refresh token
 */
export const apiLogout = async (refreshToken) => {
  const url = `${urlEndPoint}/api/v3/auth/logout`;
  const json = await callApi(url, 'POST', null, { refresh_token: refreshToken });
  return json;
};

// PROSPECTS (CRM comercial)
export const apiListProspects = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  if (params.source) query.set('source', params.source);
  if (params.city) query.set('city', params.city);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const url = `${urlEndPoint}/api/v1/prospects${qs ? `?${qs}` : ''}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiGetProspectsSummary = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.city) query.set('city', params.city);
  const qs = query.toString();
  const url = `${urlEndPoint}/api/v1/prospects/summary${qs ? `?${qs}` : ''}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiListProspectCities = async () => {
  const url = `${urlEndPoint}/api/v1/prospects/cities`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiGetProspect = async (id) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiCreateProspect = async (body) => {
  const url = `${urlEndPoint}/api/v1/prospects`;
  return callApi(url, 'POST', getToken(), body);
};

export const apiUpdateProspect = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}`;
  return callApi(url, 'PATCH', getToken(), body);
};

export const apiDeleteProspect = async (id) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}`;
  return callApi(url, 'DELETE', getToken(), null);
};

export const apiListProspectActivities = async (id) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}/activities`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiCreateProspectActivity = async (id, body) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}/activities`;
  return callApi(url, 'POST', getToken(), body);
};

export const apiLinkProspectCompany = async (id, companyId) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}/link-company`;
  return callApi(url, 'POST', getToken(), { companyId });
};

export const apiConvertProspectToTrial = async (id, body = {}) => {
  const url = `${urlEndPoint}/api/v1/prospects/${id}/convert-to-trial`;
  return callApi(url, 'POST', getToken(), body);
};

export const apiStartSernaturScrape = async (body) => {
  const url = `${urlEndPoint}/api/v1/prospects/sernatur/scrape`;
  return callApi(url, 'POST', getToken(), body);
};

export const apiGetSernaturScrapeJob = async (jobId) => {
  const url = `${urlEndPoint}/api/v1/prospects/sernatur/jobs/${jobId}`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiCancelSernaturScrape = async (jobId) => {
  const url = `${urlEndPoint}/api/v1/prospects/sernatur/jobs/${jobId}/cancel`;
  return callApi(url, 'POST', getToken(), {});
};

